/**
 * ============================================================================
 * useUpdateThresholdMutation — Phase 6b frontend (Threshold Matrix Admin)
 * Owner: Claude 3 (Frontend & UX).
 * Purpose: PUT /api/v1/quality-metrics/thresholds/{metricKey} — update the
 *          green/amber/red boundaries for a single metric.
 *
 * REGULATORY:
 *   Threshold changes are GMP_CRITICAL — requires TWO distinct signature
 *   bundle ids (QUALITY_THRESHOLD_DUAL_SIGN_A / _B). The server enforces
 *   "distinct signer". This mirrors the tenant-config dual-sign pattern.
 * ============================================================================
 */

import { useMutation, type UseMutationResult } from '@tanstack/react-query';
import { apiFetch, newIdempotencyKey } from '../../api/client';
import type { MetricKey } from '../QualityMetricsDashboard/useDashboardQuery';
import type {
  MetricThreshold,
  ThresholdBoundaries,
} from './useThresholdsQuery';

export interface UpdateThresholdInput {
  metricKey: MetricKey;
  boundaries: ThresholdBoundaries;
  signatureBundleIdA: string;
  signatureBundleIdB: string;
  /** Reason the change is being made — written to the audit trail. */
  changeReason: string;
}

export function useUpdateThresholdMutation(): UseMutationResult<
  MetricThreshold,
  Error,
  UpdateThresholdInput
> {
  return useMutation<MetricThreshold, Error, UpdateThresholdInput>({
    mutationFn: ({
      metricKey,
      boundaries,
      signatureBundleIdA,
      signatureBundleIdB,
      changeReason,
    }) =>
      apiFetch<MetricThreshold>(
        `/api/v1/quality-metrics/thresholds/${encodeURIComponent(metricKey)}`,
        {
          method: 'PUT',
          body: {
            boundaries,
            change_reason: changeReason,
            signature_bundle_ids: [signatureBundleIdA, signatureBundleIdB],
          },
          idempotencyKey: newIdempotencyKey(),
        },
      ),
  });
}
